import type { MouseEvent, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { cx } from './cx'

export interface ListProps {
  'aria-label'?: string | undefined
  /** Divisori inset di 1dp tra le voci (default true). */
  divided?: boolean | undefined
  /** Altezza delle voci: la densita' dell'utente (--md-density-list-height) o un valore fisso. */
  density?: 'user' | 'compact' | undefined
  className?: string | undefined
  children: ReactNode
}

/**
 * Lista M3 Expressive: contenitore con raggio 16, voci con raggio 4 (16 ai capi e per la voce selezionata,
 * primary-container). L'altezza di riga segue la densita' scelta dall'utente (56/52/48/44).
 */
export function List({ 'aria-label': ariaLabel, divided = true, density = 'user', className, children }: ListProps) {
  return (
    <ul aria-label={ariaLabel} className={cx('md-list', density === 'compact' && 'md-list--compact', divided && 'md-list--divided', className)}>
      {children}
    </ul>
  )
}

export interface ListItemProps {
  headline: ReactNode
  supporting?: ReactNode | undefined
  overline?: ReactNode | undefined
  /** Icona (24, ma 20 qui) avatar o controllo di selezione a inizio riga. */
  leading?: ReactNode | undefined
  /** Testo, icona o controllo a fine riga. Se la riga e' interattiva non metterci altri controlli interattivi. */
  trailing?: ReactNode | undefined
  /** Con onClick la riga e' un <button>; con `to` e' un <Link>. */
  onClick?: ((e: MouseEvent<HTMLElement>) => void) | undefined
  to?: string | undefined
  selected?: boolean | undefined
  disabled?: boolean | undefined
  className?: string | undefined
}

export function ListItem({ headline, supporting, overline, leading, trailing, onClick, to, selected, disabled, className }: ListItemProps) {
  const lines = 1 + (supporting ? 1 : 0) + (overline ? 1 : 0)
  const cls = cx(
    'md-list__item',
    lines === 2 && 'md-list__item--two-line',
    lines >= 3 && 'md-list__item--three-line',
    selected && 'md-list__item--selected',
    disabled && 'md-list__item--disabled',
    className,
  )
  const body = (
    <>
      {leading ? <span className="md-list__leading">{leading}</span> : null}
      <span className="md-list__content">
        {overline ? <span className="md-list__overline">{overline}</span> : null}
        <span className="md-list__headline">{headline}</span>
        {supporting ? <span className="md-list__supporting">{supporting}</span> : null}
      </span>
      {trailing ? <span className="md-list__trailing">{trailing}</span> : null}
    </>
  )
  const current = selected ? ('true' as const) : undefined
  if (to !== undefined && !disabled) {
    return (
      <li className="md-list__li">
        <Link to={to} aria-current={current} className={cx(cls, 'md-list__item--interactive', 'md-state')}>
          {body}
        </Link>
      </li>
    )
  }
  if (onClick) {
    return (
      <li className="md-list__li">
        <button type="button" disabled={disabled} aria-current={current} className={cx(cls, 'md-list__item--interactive', 'md-state')} onClick={onClick}>
          {body}
        </button>
      </li>
    )
  }
  return (
    <li className="md-list__li">
      <div className={cls} aria-disabled={disabled || undefined}>
        {body}
      </div>
    </li>
  )
}
